import { Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FEES, FIRM, FIRM_ADDRESS, PEOPLE } from "@/lib/firm";
import { suburbHref } from "@/lib/suburb-links";
import { getConveyancingLocation } from "@/lib/conveyancing-locations";



export type LocationContent = {
  slug: string;
  name: string;
  title: string;
  metaTitle: string;
  description: string;
  lead: string;
  paragraphs: string[];
  localNotes: { t: string; d: string }[];
  suburbs: string[];
  faqs: { q: string; a: string }[];
  /** Override the office shown in the footer line (e.g. the Scarborough office). */
  officeAddress?: string;
  editorialByline?: string;
  sources?: { label: string; href: string }[];
  /** Show the named lawyer's photo and credentials near the top of the page. */
  person?: "erin-wilkinson" | "michael-klein";
};

export function LocationPage({ content, personSlug }: { content: LocationContent; personSlug?: string }) {
  const person = content.person ? PEOPLE.find((p) => p.slug === content.person) : undefined;

  return (
    <>
      <PageHero personSlug={personSlug} eyebrow={`Serving ${content.name}`} title={content.title} lead={content.lead} cta={`Enquire about your ${content.name.replace(/^the /, "")} matter`} />

      <section className="mx-auto max-w-3xl px-5 py-16">
        {person && (
          <div className="mb-10 flex flex-col gap-6 rounded-sm border border-border bg-card p-6 sm:flex-row sm:items-center">
            <img
              src={person.photo}
              alt={`${person.name}, ${person.role} at Coastside Law, ${content.name}`}
              title={`${person.name} — ${person.role}, Coastside Law ${content.name}`}
              width={224}
              height={224}
              loading="eager"
              className="h-52 w-52 shrink-0 self-center rounded-sm border border-border object-cover object-top"
            />
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground">
                Your {content.name} solicitor
              </p>
              <h2 className="mt-2 font-serif text-3xl text-foreground">{person.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {person.role} · Admitted {person.admitted} · Based in {person.based}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {person.areas.join(" · ")}. Call {FIRM.phone} and you speak with the solicitor doing
                the work.
              </p>
            </div>
          </div>
        )}

        {content.editorialByline && <p className="mb-6 text-sm text-muted-foreground">By {content.editorialByline} · Coastside Law</p>}
        {content.paragraphs.map((p) => (
          <p key={p} className="mb-5 text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}


        {content.slug === "caloundra" && (
          <p className="mt-6 border-l-2 border-primary pl-4 text-base leading-relaxed text-muted-foreground">
            Buying or selling a home? See our{" "}
            <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">Caloundra conveyancing page</Link>{" "}
            for fixed fees, contract checks and local property issues. For your will or an estate, see{" "}
            <Link to="/wills-estate-lawyers-sunshine-coast" className="font-semibold text-primary underline">Sunshine Coast wills and estate lawyers</Link>.
          </p>
        )}

        {content.slug !== "caloundra" && <div className="mt-8 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Our fixed fees</h2>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">Residential sale</dt>
              <dd className="font-serif text-xl text-foreground">
                {FEES.sale} <span className="text-xs font-sans text-muted-foreground">{FEES.saleNote}</span>
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">Residential purchase</dt>
              <dd className="font-serif text-xl text-foreground">{FEES.purchase}</dd>
            </div>
          </dl>
          <Link to="/fees" className="mt-5 inline-block text-sm font-semibold text-primary underline">
            Full fee breakdown
          </Link>
        </div>}

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          What we watch for in {content.name}
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {content.localNotes.map((n) => (
            <div key={n.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{n.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">How we help</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {content.slug === "caloundra" ? <>
            <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="rounded-sm border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground card-elevate">Caloundra conveyancing →</Link>
            <Link to="/wills/$suburb" params={{ suburb: "caloundra" }} className="rounded-sm border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground card-elevate">Caloundra wills & powers of attorney →</Link>
            <Link to="/probate" className="rounded-sm border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground card-elevate">Probate & estate administration →</Link>
          </> : <>
          {[
            { to: "/conveyancing" as const, t: "Conveyancing" },
            { to: "/wills-and-estates" as const, t: "Wills & estates" },
            { to: "/elder-law" as const, t: "Elder law" },
          ].map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="rounded-sm border border-border bg-card px-5 py-4 text-sm font-semibold text-foreground card-elevate"
            >
              {s.t} →
            </Link>
          ))}
          </>}
        </div>

        {getConveyancingLocation(content.slug) && (
          <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
            Buying or selling here specifically?{" "}
            <Link
              to="/conveyancing/$suburb"
              params={{ suburb: content.slug }}
              className="font-semibold text-primary underline"
            >
              Conveyancing {content.name} — fixed fee, what we check and what it costs
            </Link>
            .
          </p>
        )}


        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">Nearby areas: </span>
          {content.suburbs.map((s, i) => {
            const href = suburbHref(s, content.slug);
            return (
              <span key={s}>
                {i > 0 && ", "}
                {href ? (
                  <Link to={href} className="text-primary underline">
                    {s}
                  </Link>
                ) : (
                  s
                )}
              </span>
            );
          })}
          . Our office is at {content.officeAddress ?? FIRM_ADDRESS}.
        </p>


        {content.slug === "caloundra" ? <>
          <h2 className="mt-14 font-serif text-3xl text-foreground">Local legal services and fees</h2>
          <ul className="mt-5 space-y-2 text-sm">
            <li><Link to="/elder-law" className="font-semibold text-primary underline">Elder law and retirement advice</Link></li>
            <li><Link to="/probate-queensland" className="font-semibold text-primary underline">Probate grants and deceased estate administration explained</Link></li>
            <li><Link to="/fees" className="font-semibold text-primary underline">Our legal fee schedule</Link></li>
          </ul>
        </> : <>
        <h2 className="mt-14 font-serif text-3xl text-foreground">Useful reading before you sign</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/stamp-duty-calculator-qld" className="font-semibold text-primary underline">
              Queensland stamp duty calculator
            </Link>
          </li>
          <li>
            <Link to="/pexa-settlement" className="font-semibold text-primary underline">
              How PEXA settlement works
            </Link>
          </li>
          <li>
            <Link to="/what-does-a-conveyancer-do-qld" className="font-semibold text-primary underline">
              What a conveyancer does in Queensland
            </Link>
          </li>
          <li>
            <Link to="/cooling-off-period-qld" className="font-semibold text-primary underline">
              Already signed? The Queensland cooling-off period explained
            </Link>
          </li>
        </ul>
        </>}

        {content.slug === "conveyancing-sunshine-coast" && (
          <>
            <h2 className="mt-14 font-serif text-3xl text-foreground">Our two offices</h2>
            <ul className="mt-5 space-y-2 text-sm">
              <li>
                <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">
                  Conveyancing in Caloundra
                </Link>{" "}
                — our Golden Beach office, 40 Theresa Street.
              </li>
              <li>
                <Link to="/conveyancing/$suburb" params={{ suburb: "redcliffe" }} className="font-semibold text-primary underline">
                  Conveyancing in Redcliffe
                </Link>{" "}
                — our Scarborough office, 15 Fortune Street.
              </li>
            </ul>
          </>
        )}

        {content.sources && <div className="mt-10"><h2 className="font-serif text-2xl text-foreground">Council information for your property</h2><ul className="mt-4 space-y-2">{content.sources.map((source) => <li key={source.href}><a href={source.href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{source.label}</a></li>)}</ul></div>}
        <h2 className="mt-14 font-serif text-3xl text-foreground">Local questions</h2>
        <div className="mt-8">
          <FaqList items={content.faqs} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading={`Get a quote for your ${content.name} matter`} suburb={content.name} />
        </div>
      </section>

      <CtaBand
        title={`Local to ${content.name}`}
        body={`Call ${FIRM.phone} and speak to a lawyer at ${FIRM.name} — not a call centre.`}
      />
      <div className="h-20" />
    </>
  );
}
